(() => {
  if (customElements.get('oilogic-accordion')) return;

/*
 * <oilogic-accordion> — progressive enhancement for a single FAQ accordion.
 *
 * The markup is a native <details>/<summary>, so it is fully accessible and
 * works with no JS at all. This element only adds:
 *   1. A smooth open/close height + fade animation (skipped under
 *      prefers-reduced-motion, where the browser's instant toggle is kept).
 *   2. Optional "single open" behavior: when the closest [data-accordion-group]
 *      carries [data-single-open], opening one item closes its siblings.
 *
 * State lives on the native <details open> attribute; the +/- icon swap is
 * pure CSS keyed off [open]. Events are namespaced oilogic:accordion:*.
 */
class OilogicAccordion extends HTMLElement {
  #ctrl = new AbortController();
  #animation = null;

  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.details?.querySelector('summary');
    this.panel = this.querySelector('[data-accordion-content]');
    if (!this.details || !this.summary || !this.panel) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const { signal } = this.#ctrl;

    this.summary.addEventListener('click', (event) => this.#onSummaryClick(event), { signal });
    this.details.addEventListener('toggle', () => this.#onToggle(), { signal });
  }

  disconnectedCallback() {
    this.#animation?.cancel();
    this.#ctrl.abort();
  }

  // Public: used by sibling accordions to collapse this one in single-open mode.
  close() {
    if (!this.details?.open) return;
    this.#animation?.cancel();
    if (this.reduceMotion) {
      this.details.open = false;
      return;
    }
    this.#animateClose();
  }

  #onSummaryClick(event) {
    // Let the browser handle the toggle natively when motion is reduced.
    if (this.reduceMotion) return;

    event.preventDefault();
    this.#animation?.cancel();

    if (this.details.open) this.#animateClose();
    else this.#animateOpen();
  }

  #onToggle() {
    if (!this.details.open) return;
    this.dispatchEvent(new CustomEvent('oilogic:accordion:open', { bubbles: true }));
    this.#closeSiblings();
  }

  #closeSiblings() {
    const group = this.closest('[data-accordion-group]');
    if (!group || !group.hasAttribute('data-single-open')) return;

    group.querySelectorAll('oilogic-accordion').forEach((item) => {
      if (item !== this) item.close();
    });
  }

  #animateOpen() {
    this.details.open = true; // fires 'toggle' → closes siblings
    const end = this.panel.scrollHeight;

    this.#animation = this.panel.animate(
      { height: ['0px', `${end}px`], opacity: [0, 1] },
      { duration: 280, easing: 'ease' }
    );
    this.#animation.onfinish = () => {
      this.panel.style.height = 'auto';
      this.#animation = null;
    };
  }

  #animateClose() {
    const start = this.panel.scrollHeight;
    this.panel.style.height = `${start}px`;

    this.#animation = this.panel.animate(
      { height: [`${start}px`, '0px'], opacity: [1, 0] },
      { duration: 240, easing: 'ease' }
    );
    this.#animation.onfinish = () => {
      this.details.open = false;
      this.panel.style.height = '';
      this.#animation = null;
    };
  }
}

customElements.define('oilogic-accordion', OilogicAccordion);
})();
